import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, readSessionIdFromHash } from "@/lib/api";
import { safeNext } from "@/lib/nav";

/**
 * The landing spot after signing in.
 *
 * Sign-in hands back a one-time id in the URL fragment. This screen spends it on the server,
 * then sends the person where they were going. Every path ends in a replace-navigation, so the
 * id never lingers in the address bar or in browser history.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const spent = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Guard synchronously: this effect must run once even under StrictMode double-invocation,
    // because the id can only be spent once.
    if (spent.current) return;
    spent.current = true;

    const params = new URLSearchParams(String(location.hash).replace(/^#/, ""));
    const sessionId = readSessionIdFromHash(location.hash);
    const next = safeNext(params.get("next") || location.pathname || "/");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
        await refresh();
        navigate(next, { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const reason = typeof detail === "object" ? detail?.reason : null;
        if (reason) {
          const query = new URLSearchParams({ reason });
          if (detail?.email) query.set("email", detail.email);
          navigate(`/not-invited?${query.toString()}`, { replace: true });
          return;
        }
        setError(errorMessage(e, "Sign-in could not be completed. Please try again."));
        await refresh().catch(() => {});
      }
    })();
  }, [location.hash, location.pathname, navigate, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rp-brand-panel rp-noise relative overflow-hidden rounded-t-2xl p-6">
          <Wordmark size="md" />
        </div>
        <div className="rounded-b-2xl border border-t-0 border-[hsl(var(--border))] bg-white p-6 sm:p-8">
          {error ? (
            <div data-testid="auth-callback-error">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--warning))]">
                <TriangleAlert className="h-5 w-5" aria-hidden="true" />
              </span>
              <h1 className="mt-4 text-xl font-semibold tracking-tight">
                That sign-in did not finish
              </h1>
              <p className="mt-3 text-sm leading-6 text-[hsl(var(--muted-foreground))]">{error}</p>
              <Button
                className="mt-6 bg-[hsl(var(--rp-navy))] hover:bg-[hsl(var(--rp-navy-2))]"
                onClick={() => navigate("/login", { replace: true })}
                data-testid="auth-callback-retry"
              >
                Back to sign in
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3" data-testid="auth-callback-working">
              <Loader2
                className="h-5 w-5 animate-spin text-[hsl(var(--rp-navy))]"
                aria-hidden="true"
              />
              <p className="text-sm font-medium">Signing you in&hellip;</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
